import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { theme as T } from '../../theme';
import DateRangePopover, { QuickSelectOption } from './DateRangePopover';
import { useStoreTimeZone, useServerClock } from '../../utils/dateTimeUtils';
import {
  buildPeriodContext,
  resolvePeriod,
  stepPeriod,
  canStepForward,
  ResolvedPeriod,
} from '../../utils/reportPeriod';

/**
 * Quiet period after the last stepper click before the selection is committed upstream.
 * Holding the stepper down therefore fires ONE data fetch for the period you land on
 * rather than a queue of abandoned requests for every period you passed through.
 */
const STEP_DEBOUNCE_MS = 350;

export interface DateRangeControlProps {
  variant: 'desktop' | 'mobile';
  /** Stored filter token — 'Today', '7D', 'custom:from:to', … */
  value: string;
  onChange: (token: string) => void;
  /** Per-report quick-select set; the component itself holds no report-specific logic. */
  quickSelects: QuickSelectOption[];
  /** Cap the picker at yesterday (reports that cannot serve the live day). */
  excludeToday?: boolean;
  /** Allow selecting future days (Reservations). */
  allowFutureDates?: boolean;
}

/**
 * The unified date control (GLOBAL-DP-1) — replaces the date-preset pill row on every
 * report.
 *
 * Two lines of text: the selected period, and the basis it is measured against. Stating
 * the basis once here is what lets the twelve KPI cards below drop their repeated
 * "vs Yesterday" label (GLOBAL-DP-4).
 *
 * The ‹ › steppers move the selection by one whole period. They are NOT the same control
 * as the month navigation inside the calendar overlay — those only move the visible month
 * and never touch the selection. The two share no state.
 */
export default function DateRangeControl({
  variant,
  value,
  onChange,
  quickSelects,
  excludeToday = false,
  allowFutureDates = false,
}: DateRangeControlProps) {
  const isMobile = variant === 'mobile';
  const storeTz = useStoreTimeZone();
  // ZAR-1662 — the truncation hour in the comparison line comes from the server's clock
  // once `snapshot-details/comparison` has answered. Subscribing keeps that line in the
  // memo deps below, so the label recomputes when the reading lands after first paint
  // (and again whenever a refetch moves it into the next hour).
  const serverClock = useServerClock();
  const [open, setOpen] = useState(false);

  // Stepper debouncing: `pending` is what the control DISPLAYS, `value` is what the report
  // has actually fetched. Holding it until the parent echoes the token back stops the
  // label flickering to the old period for a frame after each commit.
  const [pending, setPending] = useState<string | null>(null);
  const flushRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (pending !== null && value === pending) setPending(null);
  }, [value, pending]);

  useEffect(() => () => { if (flushRef.current) clearTimeout(flushRef.current); }, []);

  const token = pending ?? value;

  const ctx = useMemo(() => buildPeriodContext(storeTz), [storeTz, token, serverClock]); // eslint-disable-line react-hooks/exhaustive-deps
  const period: ResolvedPeriod = useMemo(() => resolvePeriod(token, ctx, storeTz), [token, ctx, storeTz]);

  /** Commit immediately — quick-select and Apply are explicit, deliberate actions. */
  const commitNow = useCallback((next: string) => {
    if (flushRef.current) clearTimeout(flushRef.current);
    setPending(null);
    onChange(next);
    setOpen(false);
  }, [onChange]);

  /** Commit after the stepper settles. */
  const commitDebounced = useCallback((next: string) => {
    setPending(next);
    if (flushRef.current) clearTimeout(flushRef.current);
    flushRef.current = setTimeout(() => onChange(next), STEP_DEBOUNCE_MS);
  }, [onChange]);

  const stepBack = () => {
    const next = stepPeriod(period, -1, ctx);
    if (next) commitDebounced(next);
  };
  const stepForward = () => {
    const next = stepPeriod(period, 1, ctx);
    if (next) commitDebounced(next);
  };

  // Forward is disabled whenever the next period would end in the future — the selection
  // is never silently clamped to a shorter window.
  const forwardEnabled = allowFutureDates || canStepForward(period, ctx);

  const pickerMaxDate = allowFutureDates
    ? undefined
    : (excludeToday ? ctx.anchorISO : ctx.todayISO);

  const stepperStyle = (enabled: boolean): React.CSSProperties => ({
    width: isMobile ? 32 : 28,
    height: isMobile ? 32 : 28,
    flexShrink: 0,
    borderRadius: 8,
    border: `1px solid ${T.border}`,
    background: T.surface,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: enabled ? 'pointer' : 'not-allowed',
    opacity: enabled ? 1 : 0.4,
    padding: 0,
  });

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? 8 : 6,
        width: isMobile ? '100%' : undefined,
      }}
    >
      <button
        type="button"
        onClick={stepBack}
        aria-label="Previous period"
        style={stepperStyle(true)}
      >
        <span className="material-symbols-rounded" style={{ fontSize: 18, color: T.t2 }}>chevron_left</span>
      </button>

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        style={{
          flex: isMobile ? 1 : undefined,
          minWidth: isMobile ? 0 : 190,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          textAlign: 'left',
          padding: isMobile ? '7px 14px' : '5px 14px',
          borderRadius: 10,
          border: `1px solid ${open ? T.accent : T.border}`,
          background: open ? T.accentSoft : T.surface,
          cursor: 'pointer',
          fontFamily: T.f,
        }}
      >
        <span className="material-symbols-rounded" style={{ fontSize: isMobile ? 19 : 18, color: T.accent, flexShrink: 0 }}>
          calendar_month
        </span>
        <span style={{ minWidth: 0 }}>
          <span
            style={{
              display: 'block',
              fontSize: isMobile ? 14 : 13,
              fontWeight: 700,
              color: T.t1,
              lineHeight: 1.25,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {period.selectionLabel}
          </span>
          <span
            style={{
              display: 'block',
              fontSize: isMobile ? 11.5 : 11,
              fontWeight: 500,
              color: T.t3,
              lineHeight: 1.25,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {period.comparisonLabel}
          </span>
        </span>
      </button>

      <button
        type="button"
        onClick={forwardEnabled ? stepForward : undefined}
        disabled={!forwardEnabled}
        aria-label="Next period"
        style={stepperStyle(forwardEnabled)}
      >
        <span className="material-symbols-rounded" style={{ fontSize: 18, color: T.t2 }}>chevron_right</span>
      </button>

      {open && (
        <DateRangePopover
          variant={variant}
          onClose={() => setOpen(false)}
          activeStart={period.startDate}
          activeEnd={period.endDate}
          quickSelects={quickSelects}
          onSelectToken={commitNow}
          onApplyRange={commitNow}
          maxDate={pickerMaxDate}
          todayISO={ctx.todayISO}
          storeTz={storeTz}
        />
      )}
    </div>
  );
}
